/**
 * Form Builder Canvas (P2.1-S5 polish).
 *
 * Header form (Tên + Mô tả) giờ collapsible — default thu gọn khi đã có
 * ≥ 1 field, expand khi chưa có để onboarding tốt hơn.
 */
import { ChevronDown } from "lucide-react";
import * as React from "react";

import type { FieldType, FormFieldSchema } from "@/api/forms";
import { FieldNode } from "@/components/form-builder/FieldNode";
import { cn } from "@/lib/utils";
import { type FieldPath, useFormBuilderStore } from "@/stores/formBuilder";

interface CanvasProps {
  onOverride?: (path: FieldPath, field: FormFieldSchema) => void;
}

export function Canvas({ onOverride }: CanvasProps = {}): JSX.Element {
  const schema = useFormBuilderStore((s) => s.schema);
  const formName = useFormBuilderStore((s) => s.formName);
  const formDescription = useFormBuilderStore((s) => s.formDescription);
  const setMeta = useFormBuilderStore((s) => s.setMeta);
  const addField = useFormBuilderStore((s) => s.addField);
  const selectField = useFormBuilderStore((s) => s.selectField);

  const hasFields = Boolean(schema && schema.fields.length > 0);
  const [headerOpen, setHeaderOpen] = React.useState(!hasFields);

  // Khi schema chuyển từ "có fields" → "chưa có" (vd reset), re-expand header
  React.useEffect(() => {
    if (!hasFields) setHeaderOpen(true);
  }, [hasFields]);

  const onDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    const t = e.dataTransfer.getData("application/x-field-type") as FieldType;
    if (t) addField(null, t);
  };

  if (!schema) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        Đang tải...
      </div>
    );
  }

  return (
    <main
      className="flex-1 overflow-y-auto bg-muted/40"
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDrop={onDrop}
      onClick={() => selectField(null)}
    >
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        {/* Form header — collapsible */}
        <section
          className="rounded-md border bg-background"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setHeaderOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-muted/30 rounded-md transition-colors"
            aria-expanded={headerOpen}
          >
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Form
              </div>
              <div className="font-semibold text-sm truncate">
                {formName || "(chưa đặt tên)"}
              </div>
              {formDescription ? (
                <div className="text-xs text-muted-foreground truncate">
                  {formDescription}
                </div>
              ) : null}
            </div>
            <ChevronDown
              className={cn(
                "size-4 text-muted-foreground transition-transform shrink-0",
                headerOpen && "rotate-180",
              )}
            />
          </button>
          {headerOpen ? (
            <div className="px-4 pb-4 pt-1 space-y-3 border-t animate-fade-in">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Tên form
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setMeta(e.target.value, formDescription)}
                  className="w-full text-base font-semibold bg-transparent border-b border-transparent hover:border-input focus:border-primary outline-none px-1 py-0.5"
                  placeholder="Tên form..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Mô tả
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setMeta(formName, e.target.value)}
                  rows={2}
                  className="w-full text-sm bg-transparent border border-transparent hover:border-input focus:border-primary outline-none rounded px-2 py-1"
                  placeholder="Mô tả ngắn về form..."
                />
              </div>
            </div>
          ) : null}
        </section>

        {/* Fields */}
        <section className="space-y-2">
          {schema.fields.length === 0 ? (
            <div className="rounded-md border-2 border-dashed bg-background p-12 text-center text-muted-foreground">
              <p className="text-sm">Chưa có field nào</p>
              <p className="text-xs mt-1">
                Click hoặc kéo field từ palette bên trái.
              </p>
            </div>
          ) : (
            schema.fields.map((f, i) => (
              <FieldNode
                key={`${f.name}-${i}`}
                field={f}
                path={String(i)}
                onOverride={onOverride}
              />
            ))
          )}
        </section>
      </div>
    </main>
  );
}
